import React, { useEffect, useRef, useLayoutEffect, useState, memo, useCallback } from 'react';
import { 
  Trash2, 
  ArrowRightLeft, 
  Repeat, 
  CornerUpRight, 
  Activity, 
  Minus, 
  Workflow
} from 'lucide-react';
import { PATH_STYLE_CONFIG } from '../constants';

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  onDelete: () => void;
  onReverse: () => void;
  onToggleBidirectional: () => void;
  onChangeType: (type: string) => void;
  isBidirectional: boolean;
  currentType: string;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  x,
  y,
  onClose,
  onDelete,
  onReverse,
  onToggleBidirectional,
  onChangeType,
  isBidirectional,
  currentType
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState({ x, y });

  // Adjust position to stay within viewport
  useLayoutEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newX = x;
      let newY = y;

      // Check right edge
      if (x + rect.width > viewportWidth) {
        newX = x - rect.width;
      }

      // Check bottom edge
      if (y + rect.height > viewportHeight) {
        newY = y - rect.height;
      }
      
      setAdjustedPos({ x: newX, y: newY });
    }
  }, [x, y]);

  // Close if clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Add small delay to prevent immediate closing from the triggering click
    const timer = setTimeout(() => {
        document.addEventListener('click', handleClick);
        document.addEventListener('contextmenu', handleClick);
    }, 50);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('contextmenu', handleClick);
    };
  }, [onClose]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!menuRef.current) return;
      
      // Close on Escape
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      
      // Keyboard shortcuts
      if (e.key === 'd' || e.key === 'D') {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          onDelete();
        }
      } else if (e.key === 'r' || e.key === 'R') {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          onReverse();
        }
      } else if (e.key === 'b' || e.key === 'B') {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          onToggleBidirectional();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onDelete, onReverse, onToggleBidirectional]);

  return (
    <div
      ref={menuRef}
      style={{ 
        top: adjustedPos.y, 
        left: adjustedPos.x,
        transformOrigin: 'top left',
        animationDuration: '150ms',
      }}
      className="absolute z-[100] bg-white border border-gray-200 rounded-lg shadow-xl min-w-[220px] py-1 animate-scale-in will-change-transform"
      onContextMenu={(e) => e.preventDefault()}
      role="menu"
      aria-label="Connection options menu"
    >
      <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 mb-1">
        Connection Options
      </div>

      {/* Direction Controls */}
      <div className="px-3 py-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider">
        Direction
      </div>
      <button
        onClick={onToggleBidirectional}
        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-all duration-150 active:scale-95"
        title="Toggle bidirectional (Ctrl+B)"
        aria-label={isBidirectional ? 'Make connection unidirectional' : 'Make connection bidirectional'}
      >
        <Repeat size={14} />
        <span className="flex-1">{isBidirectional ? 'Make Unidirectional' : 'Make Bidirectional'}</span>
        <span className="text-xs text-gray-400">Ctrl+B</span>
      </button>

      <button
        onClick={onReverse}
        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-all duration-150 active:scale-95"
        title="Reverse connection direction (Ctrl+R)"
        aria-label="Reverse connection flow direction"
      >
        <ArrowRightLeft size={14} />
        <span className="flex-1">Reverse Flow</span>
        <span className="text-xs text-gray-400">Ctrl+R</span>
      </button>

      <div className="my-1 border-t border-gray-100"></div>
      <div className="px-3 py-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider">
        Path Style
      </div>

      {Object.entries(PATH_STYLE_CONFIG).map(([type, config]) => {
        const IconComponent = config.icon;
        return (
          <button
            key={type}
            onClick={() => onChangeType(type)}
            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-all duration-150 active:scale-95
              ${currentType === type ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}
            `}
            title={config.description}
          >
            <IconComponent size={14} />
            {config.label}
          </button>
        );
      })}

      <div className="my-1 border-t border-gray-100"></div>

      {/* Actions */}
      <div className="px-3 py-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider">
        Actions
      </div>
      <button
        onClick={onDelete}
        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-all duration-150 active:scale-95"
        title="Delete connection (Ctrl+D)"
        aria-label="Delete this connection"
      >
        <Trash2 size={14} />
        <span className="flex-1">Delete Connection</span>
        <span className="text-xs text-gray-400">Ctrl+D</span>
      </button>
    </div>
  );
};

export default memo(ContextMenu);